import { MerchantProductType } from 'types/product';
import { EditDetailProductType, FlattedCategoryType, MerchantType } from 'types/components/shared';
import { CategoryType } from 'types/components/categories';
import { characters } from 'utils/characters';
import axios from './axios';
import { AllMerchantsProductDataType } from 'types/views/products/allMerchantsProductData';
export function hexIsLight(color: string) {
    const hex = color.replace('#', '');

    const cr = parseInt(hex.substring(0, 2), 16);
    const cg = parseInt(hex.substring(2, 4), 16);
    const cb = parseInt(hex.substring(4, 6), 16);

    const brightness = (cr * 299 + cg * 587 + cb * 114) / 1000;

    return brightness > 150;
}

export const filterCategories = (categories: CategoryType[] | undefined, searchText: string, isMatch?: boolean): CategoryType[] => {
    if (!categories) return [];
    if (!searchText) return categories;
    let filteredCategories: CategoryType[] = [];

    categories.forEach((item) => {
        const { children, ...rest } = item;
        const stringifiedCategory = JSON.stringify(rest).toLowerCase();

        if (isMatch) {
            if (rest.id.toString() === searchText) {
                filteredCategories.push(item);
            }
        } else {
            if (stringifiedCategory.includes(searchText.toLowerCase())) {
                filteredCategories.push(item);
            }
        }

        if (children?.length) {
            const filteredChildren = filterCategories(item.children, searchText, isMatch);
            filteredCategories = filteredCategories.concat(filteredChildren);
        }
    });

    return filteredCategories;
};

export const flatCategories = (categories: CategoryType[] | undefined): FlattedCategoryType[] => {
    if (!categories) return [];
    let toReturn: FlattedCategoryType[] = [];

    const flattedCat = (cat: CategoryType, level: number): FlattedCategoryType => ({
        id: cat.id,
        name: cat.name,
        title: cat.title,
        description: cat.description,
        hasChildren: Boolean(cat?.children?.length),
        level
    });

    const flatChild = (cat: CategoryType, level: number) => {
        const father = flattedCat(cat, level);

        toReturn = [...toReturn, father];

        if (cat.children) {
            cat.children.forEach((itemB) => {
                flatChild(itemB, level + 1);
            });
        }
    };

    categories.forEach((itemA) => {
        flatChild(itemA, 1);
    });

    return toReturn;
};

export const flatCategoriesWithBreadcrumb = (categories: CategoryType[] | undefined): FlattedCategoryType[] => {
    if (!categories) return [];
    const result: FlattedCategoryType[] = [];

    const recurse = (cat: CategoryType, level: number, path: string[]) => {
        const thisPath = [...path, cat.name];
        result.push({
            id: cat.id,
            name: cat.name,
            title: cat.title,
            description: cat.description,
            hasChildren: Boolean(cat.children?.length),
            level,
            breadcrumb: thisPath.join(' > ')
        });
        cat.children?.forEach((child) => recurse(child, level + 1, thisPath));
    };

    categories.forEach((root) => recurse(root, 1, []));
    return result;
};

export function stringToColor(string: string) {
    let hash = 0;
    let y;

    /* eslint-disable no-bitwise */
    for (y = 0; y < string.length; y += 1) {
        hash = string.charCodeAt(y) + string.charCodeAt(y) + ((hash << 4) - hash);
    }

    let color = '';
    let i;

    for (i = 0; i < 3; i += 1) {
        let value = (hash >> (i * 8)) & 0xff;

        value = value > 0 ? value : string.length;

        color += `${(value + value * 10).toString(16)}`.replaceAll('.', '');
    }
    const colorString = `#${color.substring(0, 6)}`;

    /* eslint-enable no-bitwise */
    return colorString;
}

export const generateRandomString = (num: number) => {
    let result1 = '';
    const charactersLength = characters.length;
    for (let i = 0; i < num; i += 1) {
        result1 += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result1;
};

export const createMerchantList = (data: { [key: string]: any }): MerchantType[] => {
    let list: MerchantType[] = [];

    // add father
    const father: MerchantType = {
        name: data.name,
        status: data.status,
        merchantId: data.merchantId,
        isFather: true
    };

    data.multiCatalogMerchant.merchants.forEach((item: { [key: string]: any }) => {
        const newMerch: MerchantType = {
            name: item.name,
            status: item.status,
            merchantId: item.merchantId,
            isFather: false
        };

        list = [...list, newMerch];
    });

    return [father, ...list];
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function removeDuplicateMerchants(data: MerchantProductType[]): MerchantProductType[] {
    const seenMerchants: Record<number, boolean> = {};

    return data.filter((merchant) => {
        if (seenMerchants[merchant.merchantId]) {
            return false;
        }
        seenMerchants[merchant.merchantId] = true;
        return true;
    });
}

export const urlIsImage = (urlString: string) => {
    const imageRegex = /^https?:\/\/.*\.(png|jpg|jpeg|gif|bmp|svg)$/i;
    const isImageURL = imageRegex.test(urlString);

    return isImageURL;
};

const typeMap: { [key: string]: string } = {
    Usuarios: 'users',
    Perfiles: 'profiles',
    Productos: 'products',
    Collections: 'collections',
    Categorías: 'categories',
    Marcas: 'brands',
    Facets: 'facets',
    Proveedores: 'vendors',
    'Actualización Masiva': 'massive_load',
    'Bitácora de Cambios': 'change_log',
    Detalle: 'health-content',
    Warranties: 'Warranties',
    'Assets Management': 'assets-management'
};

export const getNewRouteType = (type: string): string => typeMap[type] || '*';

export function expToken(token: string) {
    const base64Url = token.split('.')[1] || '';
    const base64WithPlus = base64Url.replace(/-/g, '+');
    const base64 = base64WithPlus.replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('')
    );

    const parsed = JSON.parse(jsonPayload);

    return parsed.exp * 1000;
}

/**
 *
 * @param asyncFn Function
 *
 * Example: retryAsync(() => myFunc(param))
 *
 * Example: retryAsync(myFunc)
 *
 * @param maxAttempts
 * @default 2
 * @returns T
 */
export async function retryAsync<T>(asyncFn: () => Promise<T>, maxAttempts: number = 2): Promise<T> {
    let attempts = 0;
    let lastError: any;

    while (attempts < maxAttempts) {
        try {
            return await asyncFn();
        } catch (error) {
            lastError = error;
            attempts++;
        }
    }

    throw lastError;
}

export const formatBytes = (bytes: number, dm = 2) => {
    if (bytes === 0) return '0 Bytes';
    var k = 1024,
        sizes = [`${bytes <= 1 ? 'Byte' : 'Bytes'}`, 'KB', 'MB', 'GB', 'TB'],
        i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const calculatePercentage = (part: number, total: number): number => (total === 0 ? 0 : +((part / total) * 100).toFixed(2));

export async function fetchFiles(urls: string[]): Promise<File[]> {
    const filePromises = urls.map(async (url, index) => {
        try {
            const response = await axios.get(url, {
                responseType: 'blob'
            });

            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const fileName = url.split('/').pop() || `file_${index}`;

            return new File([response.data], fileName, { type: contentType });
        } catch (error) {
            return null;
        }
    });

    const files = await Promise.all(filePromises);
    return files.filter((file): file is File => file !== null); // Filtra archivos válidos
}

export const filterEans = (
    originalProductData: AllMerchantsProductDataType<EditDetailProductType>[],
    currentProductData: AllMerchantsProductDataType<EditDetailProductType>[],
    merchantId: number
) => {
    const toList = (v: unknown): string[] =>
        Array.isArray(v)
            ? v
                  .map(String)
                  .map((s) => s.trim())
                  .filter(Boolean)
            : typeof v === 'string'
            ? v
                  .split(/[,\s]+/)
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [];

    const uniq = (arr: string[]) => Array.from(new Set(arr));
    const clean = (v: unknown) => uniq(toList(v));

    const originalEans = clean(originalProductData?.find((p) => p.merchantId === merchantId)?.detailProduct?.sku?.additionalEans ?? []);

    const currentEans = clean(currentProductData?.find((p) => p.merchantId === merchantId)?.detailProduct?.sku?.additionalEans ?? []);

    const isSame = originalEans.length === currentEans.length && originalEans.every((ean) => currentEans.includes(ean));

    if (isSame) return null;

    return {
        updatedEans: currentEans,
        eansToDelete: originalEans.filter((ean) => !currentEans.includes(ean))
    };
};

export const generateFileFormUrl = async (
    url: string,
    format?: string // Ej: 'image/png', 'image/jpeg'
): Promise<File | undefined> => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();

        const finalType = format || blob.type;

        let filename = url.split('/').pop()?.split('?')[0] || 'image';

        if (format) {
            const extension = format.split('/')[1];

            const nameWithoutExtension = filename.replace(/\.[^/.]+$/, '');
            filename = `${nameWithoutExtension}.${extension}`;
        } else if (!filename.includes('.')) {
            filename += '.jpg';
        }

        const file = new File([blob], filename, { type: finalType });

        return file;
    } catch (err) {
        console.error(`Error al descargar imagen desde ${url}`, err);
        return undefined;
    }
};

export const getBase64Img = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!(file instanceof File)) {
            return reject(new Error('Invalid input: Expected a File or Blob object.'));
        }

        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            } else {
                reject(new Error('FileReader did not return a string result.'));
            }
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsDataURL(file);
    });
};

export const toSnakeCase = (text: string): string => {
    return text
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
        .toLowerCase();
};
